"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MEGAPIXEL_TIERS = exports.MEDIA_PICKER_PAGE_SIZE = exports.MEDIA_PAGE_SIZE = exports.MEDIA_FORMAT_MIME = exports.MEDIA_FORMAT_FILTERS = exports.GENERATION_REFERENCE_MIME_TYPES = exports.POSTABLE_MIMES_LABEL = exports.POSTABLE_MIMES = exports.DOCUMENT_TITLE_MAX_LENGTH = exports.LINKEDIN_DOCUMENT_MAX_PAGES = exports.LINKEDIN_DOCUMENT_MAX_BYTES = exports.DOCUMENT_MIME = exports.UPLOADABLE_MEDIA_PREFIXES = exports.MEDIA_UPLOAD_MAX_LABEL = exports.MEDIA_UPLOAD_MAX_BYTES = exports.MEDIA_BULK_MAX_ITEMS = exports.MEDIA_TRASH_RETENTION_DAYS = void 0;
exports.isUploadableMime = isUploadableMime;
exports.isDocumentMime = isDocumentMime;
exports.documentTitleFromFileName = documentTitleFromFileName;
exports.isPostableMime = isPostableMime;
exports.isGenerationReferenceMime = isGenerationReferenceMime;
/**
 * How long a trashed Asset stays recoverable in the Media Library trash before it is purged —
 * the row AND its stored blob. Deliberately the same window as the campaign/post Trash
 * (`TRASH_RETENTION_DAYS` in `content.ts`) so users only have one number to remember; they are
 * separate constants because they guard separate purge jobs.
 */
exports.MEDIA_TRASH_RETENTION_DAYS = 30;
/** The most items one bulk action (move / delete / download) may target. */
exports.MEDIA_BULK_MAX_ITEMS = 200;
/**
 * Hard ceiling on an uploaded file, in bytes.
 *
 * Lives here because the upload modal now validates before sending: a 200 MB file that fails after
 * three minutes of uploading is the same rejection delivered as badly as possible. Client and server
 * must therefore agree on the number, and two copies of 100 MB in two languages do not stay agreed.
 */
exports.MEDIA_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;
/** Human form of {@link MEDIA_UPLOAD_MAX_BYTES}, for UI copy that must match the rule. */
exports.MEDIA_UPLOAD_MAX_LABEL = '100MB';
/** Broad media classes the library accepts, matched on the mime type's prefix. */
exports.UPLOADABLE_MEDIA_PREFIXES = ['image', 'video', 'audio'];
/**
 * Whether a file's mime type may be uploaded to the Media Library.
 *
 * A PREFIX test, unlike the reference-image allowlist above, because the library is a general store:
 * it holds SVG templates and design-tool exports that no image model can read. The narrow allowlist
 * is a property of GENERATION, not of storage.
 *
 * Plus exactly ONE document type, the PDF a LinkedIn carousel is made from (DOCUMENT_MIME). Exact,
 * never the `application/` prefix, which would admit every Office file, archive and executable.
 */
function isUploadableMime(mimeType) {
    if (!mimeType)
        return false;
    // Browsers append parameters, so drop them: 'video/webm; codecs="vp8, vorbis"'.
    const [type, subtype] = mimeType.split(';')[0].trim().toLowerCase().split('/');
    // A subtype is REQUIRED. The server's own check was a bare `split('/')[0]`, which let the string
    // 'image' — not a mime type at all — through. No browser sends that (it sends '' for a type it
    // doesn't know), so requiring it costs nothing and stops a hand-crafted request storing a file
    // whose type we cannot actually name.
    if (!subtype)
        return false;
    if (`${type}/${subtype}` === exports.DOCUMENT_MIME)
        return true;
    return exports.UPLOADABLE_MEDIA_PREFIXES.includes(type);
}
/**
 * The one document a post can carry: a PDF, which LinkedIn shows as a swipeable carousel of its
 * pages. LinkedIn also takes PowerPoint and Word files, but a PDF is what was asked for, and the
 * only document the page counter and first-page preview understand.
 */
exports.DOCUMENT_MIME = 'application/pdf';
/** Whether this file is a PDF document (a LinkedIn carousel), as opposed to a photo or video. */
function isDocumentMime(mimeType) {
    if (!mimeType)
        return false;
    return mimeType.split(';')[0].trim().toLowerCase() === exports.DOCUMENT_MIME;
}
/**
 * LinkedIn's ceilings for a document post, from its Documents API: "The file size can't exceed
 * 100MB and 300 pages." Decimal megabytes - 100,000,000 bytes - the reading that can't be wrong,
 * and just under the library's own 100 MiB upload cap, so a PDF the library would store but
 * LinkedIn would refuse is refused at upload instead of at publish.
 */
exports.LINKEDIN_DOCUMENT_MAX_BYTES = 100_000_000;
exports.LINKEDIN_DOCUMENT_MAX_PAGES = 300;
/**
 * The longest document title kept. LinkedIn REQUIRES a title on a document post - it heads the
 * carousel - but documents no maximum for an organic post; 200 is the ceiling video titles already
 * use here, and LinkedIn's own advice for document ads is to stay under 70 to avoid truncation.
 */
exports.DOCUMENT_TITLE_MAX_LENGTH = 200;
/**
 * A readable default title from a file's name: "Q3 carousel.pdf" becomes "Q3 carousel". The title
 * is shown to everyone who sees the post, so the extension is noise; the author can change it.
 */
function documentTitleFromFileName(name) {
    if (!name)
        return '';
    const base = name.split(/[\\/]/).pop() ?? '';
    return base
        .replace(/\.pdf$/i, '')
        .trim()
        .slice(0, exports.DOCUMENT_TITLE_MAX_LENGTH);
}
/**
 * Mime types a social platform will actually accept in a post.
 *
 * An EXACT list, not a prefix test, and that is the whole point. Storage is deliberately broad — the
 * library holds SVGs, design-tool exports and whatever else someone wants to keep — but publishing is
 * narrow, because every platform takes raster images and MP4 and nothing else.
 *
 * The two rules were the same `image/` prefix check until Generate Design started writing `.svg`
 * results into the library: those then appeared in the post picker, where choosing one would have
 * produced a post no platform could deliver.
 */
exports.POSTABLE_MIMES = ['image/jpeg', 'image/png', 'video/mp4'];
/** Human form of {@link POSTABLE_MIMES}, for the notice next to a picker. */
exports.POSTABLE_MIMES_LABEL = 'JPG, PNG or MP4';
/** Whether this file can be attached to a post and published. */
function isPostableMime(mimeType) {
    if (!mimeType)
        return false;
    // Parameters are dropped first: a browser sends 'video/mp4; codecs="avc1"' and that is still MP4.
    const base = mimeType.split(';')[0].trim().toLowerCase();
    return exports.POSTABLE_MIMES.includes(base);
}
/**
 * Mime types usable as a REFERENCE IMAGE for generation.
 *
 * An allowlist, not a blocklist. `image/svg+xml` carries the `image/` prefix, so it satisfies every
 * "is this an image" test in the codebase while being a document no image model can read — it was
 * appearing in the reference picker alongside real photos. PowerPoint has the same problem one level
 * out: it is a design-tool artefact that happens to live in the Media Library.
 *
 * Deliberately narrower than what the providers accept (they also take WebP): the reference card
 * tells the user "JPG or PNG", and a picker that offers a third format the label denies is the same
 * class of quiet mismatch. Widen both together or neither.
 *
 * SVG and PPTX are not unusable in general — they are Figma and Canva template formats and will get
 * their own generator. They just cannot be prompt references.
 */
exports.GENERATION_REFERENCE_MIME_TYPES = ['image/png', 'image/jpeg'];
/** Whether an asset's mime type can be sent to an image model as a reference. */
function isGenerationReferenceMime(mimeType) {
    if (!mimeType)
        return false;
    // Compare the type only: browsers and providers append parameters (`image/jpeg; charset=binary`).
    const bare = mimeType.split(';')[0].trim().toLowerCase();
    return exports.GENERATION_REFERENCE_MIME_TYPES.includes(bare);
}
/** Media Library list filters (query params on GET /assets). */
/**
 * The two design-tool formats the library filters by FORMAT rather than by media type.
 *
 * Neither can be expressed as a MediaType. `image/svg+xml` derives `mediaType: 'image'` — it
 * carries the `image/` prefix, which is the same trap `isGenerationReferenceMime` exists for — so
 * "Images" already includes every Figma export and a `type: 'image'` query cannot exclude them.
 * PPTX lands in `other` alongside every unrecognised upload. Matching the MIME is the only thing
 * that separates them.
 */
exports.MEDIA_FORMAT_FILTERS = ['svg', 'pptx'];
/** The exact MIME each format filter matches. */
exports.MEDIA_FORMAT_MIME = {
    svg: 'image/svg+xml',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};
/** Page size of the Media Library grid (`GET /assets/page`). */
exports.MEDIA_PAGE_SIZE = 60;
/** Page size of the narrower pickers: the composer's media panel and the reference-image overlay. */
exports.MEDIA_PICKER_PAGE_SIZE = 30;
/**
 * Output size as MEGAPIXELS, not "1k/2k/4k".
 *
 * The pixel labels cannot be honoured: Azure FLUX — the default generator — caps at 4 MP, so
 * 3840x2160 (8.29 MP) is permanently impossible there, and on OpenAI only ~16:9 reaches true 4K.
 * A megapixel budget is the same control expressed in a way every provider can actually satisfy.
 */
exports.MEGAPIXEL_TIERS = [1, 2, 4];
//# sourceMappingURL=asset.js.map